import {
  parseSessionSignature,
  parseTempoMarks,
  type SessionSignature,
  type TempoMark,
} from "./session-tempo";
import { countOrNull, durationOrNull, finiteNumber } from "./service-json";
import { parseCameraFramings, type CameraFramings } from "./video/camera-framing";

/**
 * L'état d'une séance, tel que le service de temps le publie.
 *
 * # Deux nombres, et aucun n'est un instant local
 *
 * `positionMs` est la position dans le morceau, `atMs` l'instant **serveur**
 * auquel elle était vraie. Une séance qui tourne avance toute seule : le
 * lecteur en déduit sa position à tout moment sans que le service ait à
 * republier quoi que ce soit. Une séance de deux heures sans pause tient en une
 * seule écriture.
 *
 * Convertir `atMs` sur l'horloge locale demande l'écart mesuré contre le
 * service — la métadonnée dit *quoi*, jamais *quand chez vous*. C'est la seule
 * chose qui ne se livre pas et qui se mesure.
 *
 * # Deux régimes, et un seul délai à lire : le sien
 *
 * **Le lead**, par défaut. Une personne joue, les autres écoutent, et seules
 * les notes du leader circulent. Chaque auditeur recule son transport du
 * trajet depuis le leader et l'entend exactement sur la grille ; le leader ne
 * recule de rien et s'entend à l'instant. C'est le seul régime où quelqu'un
 * joue sans latence — et chacun y a un délai **différent**, ce qui est
 * possible parce qu'il n'y a pas de boucle : le leader ne dépend de personne,
 * chaque auditeur s'aligne individuellement sur lui, et un auditeur sur une
 * mauvaise liaison ne pénalise plus toute la salle.
 *
 * **Le jam**, sur demande. Tout le monde joue, donc tout le monde s'influence,
 * donc le délai doit être le même pour tous : le pire trajet de la salle.
 *
 * Les deux s'excluent, et c'est le service qui s'en charge : allumer le jam
 * retire le lead, prendre le lead éteint le jam. Le lead se prend tout seul —
 * le premier qui annonce le prend s'il est vacant, et il se libère quand son
 * détenteur cesse d'annoncer. Une séance marche donc sans qu'on clique.
 *
 * Dans les deux régimes, le client n'a **aucune règle à connaître** : le
 * service calcule le délai de chacun, et chacun pose le nombre qu'on lui
 * donne. Le nombre arrive par deux chemins, et le régime dit lequel lire —
 * voir `ownDelayMs` :
 *
 * - en jam, le délai est le même pour tous, et tout le monde joue dessus : il
 *   se publie une fois pour la salle (`jamDelayMs`), qui le reçoit d'un coup ;
 * - en lead, chacun a le sien, et personne d'autre n'en a l'usage : le service
 *   le rend à chacun en réponse à son annonce (voir `announcePlayout`).
 *
 * # Pourquoi la table des pairs est partie
 *
 * Une version précédente publiait dans la métadonnée de salle une ligne par
 * participant — ses mesures et son délai —, et chacun y lisait la sienne. Tout
 * le monde recevait donc le délai de tout le monde sans en avoir l'usage, et
 * la table repartait vers toute la salle à chaque ligne qui bougeait : mesuré
 * le 2026-09-14, cent huit octets par participant, jusqu'à cinq écritures par
 * seconde, un volume qui croissait comme le carré du public et un plafond de
 * métadonnée atteint dès une vingtaine de personnes. Les mesures se demandent
 * maintenant à la route des participants, par qui ouvre le panneau.
 *
 * Plus anciennes encore, une version publiait un délai global de séance
 * (`playout_delay_ms`), qui ne pouvait pas dire un délai par personne, et une
 * autre faisait calculer ce retard par chaque client sur les annonces reçues
 * de pair à pair : trois clients tenaient trois versions de la même règle. Le
 * service voit toutes les annonces ; il calcule une fois.
 *
 * # Le mode Jam est un fait de séance
 *
 * Une version précédente en faisait un réglage personnel — un interrupteur
 * local décidait si *ce* transport suivait le délai. C'était faux : si l'un
 * attend et l'autre non, chacun entend l'autre à un instant musical différent
 * du sien ; celui qui n'attend pas est en avance sur ses partenaires dans sa
 * propre tête, il ralentit pour les attendre, et le tempo de l'ensemble
 * descend. Un mode qui se règle chacun de son côté défait précisément ce qu'il
 * promet. Il se bascule donc au service, comme la pause, et vaut pour tout le
 * monde.
 */

/**
 * Ce qu'un participant annonce de sa machine.
 *
 * Des coûts, jamais des instants. Deux servent au service à calculer les
 * délais — le trajet mesuré d'abord, la jambe en repli tant qu'aucune note
 * n'a encore été reçue ; le reste ne sert qu'à l'affichage, savoir *pourquoi*
 * un partenaire entend tard valant mieux que le constater.
 *
 * Distinct de `LivePeerInfo` : c'est ce qui **part** vers le service, et le
 * délai n'en fait pas partie — c'est lui qui le décide, pas nous.
 */
export interface LivePeerAnnouncement {
  /** Aller-retour entre ce participant et le SFU. Sa jambe, pas une distance. */
  legMs: number;
  /** Fenêtre d'anticipation de son ordonnanceur audio. Se règle chez lui, dans l'app. */
  lookAheadMs: number;
  /** Tampon de sortie de sa machine : système et matériel, hors de portée de l'app. */
  outputMs: number;
  /**
   * Le tampon que les notes reçues lui ont réellement coûté — percentile des
   * relevés récents, en millisecondes. Zéro tant qu'il n'a rien reçu.
   *
   * C'est la seule grandeur du lot qui décrive le chemin *emprunté par les
   * notes*, tampon de sortie compris, et la seule échantillonnée assez souvent
   * pour voir une excursion. Voir `lib/live/transit.ts` pour ce que la
   * jambe ne pouvait pas voir.
   */
  transitMs: number;
}

/**
 * Ce qu'un participant a annoncé, et le délai que le service lui a attribué en
 * retour : les mesures d'une ligne de la route des participants (voir
 * `fetchLiveParticipants`), que seul reçoit celui qui peut jouer.
 */
export interface LivePeerInfo extends LivePeerAnnouncement {
  /**
   * Le délai de restitution **de ce participant**, en millisecondes : ce qu'il
   * pose sur son transport. Zéro pour le leader, son trajet depuis le leader
   * pour un auditeur, le même pour tout le monde en jam.
   */
  delayMs: number;
}

export interface LiveSessionState {
  positionMs: number;
  atMs: number;
  paused: boolean;
  /** Tempo de départ de la séance, zéro quand le service n'en publie pas. */
  tempoBpm: number;
  /**
   * Les changements de tempo, rangés par position : avec `tempoBpm`, la grille
   * de la séance, la même chez tout le monde. Vide quand personne n'a changé
   * de tempo. Voir `lib/live/session-tempo.ts`.
   */
  tempoMarks: TempoMark[];
  /**
   * Le chiffrage de la séance : où tombent les barres de mesure, chez tout le
   * monde. 4/4 quand le service n'en publie pas. Voir `lib/live/session-tempo.ts`.
   */
  signature: SessionSignature;
  /**
   * Numéro d'ordre du service. Croissant, pour écarter une écriture périmée.
   *
   * ⚠️ Il n'avance **pas** sur ce qui bouge sans changer la séance : ni le
   * délai du jam, ni l'audience. Un état neuf peut donc porter la même
   * révision que le précédent. Il avance sur un changement de lead ou de
   * régime. Ne jamais dédupliquer un état sur `rev`.
   */
  rev: number;
  /**
   * Le mode Jam est-il actif pour la séance. Faux si absent.
   *
   * Il dit qui a le droit d'émettre (tout le monde, quand il est vrai), dans
   * quel sens le basculer, et où lire son délai : `jamDelayMs` quand il est
   * vrai, la réponse à sa propre annonce sinon. Voir `ownDelayMs`.
   */
  jam: boolean;
  /**
   * L'identité du leader, ou la chaîne vide quand personne ne l'a. Sans objet
   * en jam.
   */
  lead: string;
  /**
   * Les lignes de mesure du rouleau sont masquées, pour toute la séance.
   *
   * Un réglage d'écran, décidé par qui pilote et publié pour tout le monde :
   * devant un public, le rouleau est ce qu'on montre, et la trame y est un
   * décor dont l'interprète décide. Réglée chacun de son côté, elle ne faisait
   * rien de ce qu'on lui demandait — l'interprète la retirait de son seul
   * écran, le public continuait de la voir.
   *
   * Faux si absent : la trame est là. C'est ce que vaut une séance ouverte
   * avant ce champ, et ce que fait le studio hors récital.
   */
  measuresHidden: boolean;
  /**
   * Le rideau est baissé : plus rien ne sort de la scène.
   *
   * Un fait de séance, publié pour que tout le monde soit d'accord : l'émetteur
   * le lit pour se taire, chaque récepteur pour écarter ce qui serait encore en
   * vol, et un téléphone qui filme pour arrêter sa caméra sans que personne ait
   * à le lui dire.
   *
   * **Ce n'est pas la pause.** `paused` fige la grille de temps de toute la
   * salle, l'interprète compris. Le rideau n'y touche pas : le temps coule,
   * l'interprète joue, voit, cadre une deuxième caméra — seule la diffusion
   * s'arrête.
   *
   * Faux si absent : la scène est à l'antenne.
   */
  curtain: boolean;
  /**
   * Le chat est fermé : la salle ne peut plus rien y dire, l'interprète si.
   *
   * Un réglage de séance comme le rideau, publié pour que celui qui arrive
   * après le geste trouve le chat dans le même état que les autres — ce
   * qu'aucun paquet de données ne saurait promettre à un arrivant. Le service
   * refuse les messages de la salle tant qu'il est fermé ; les cœurs passent.
   *
   * Faux si absent : la salle parle.
   */
  chatClosed: boolean;
  /**
   * La prise en cours : 1 dès l'ouverture, puis 2, 3…
   *
   * # Une prise est un fait, pas une déduction
   *
   * Quelqu'un qui s'enregistre seul recommence : il joue, se trompe, reprend la
   * chanson du début, et recommence jusqu'à la version qu'il gardera. Découper
   * cet enregistrement, c'est savoir *où* il a repris — et cette frontière naît
   * d'un signal de l'interprète (voir `sendNextTake`), jamais d'une analyse du
   * jeu faite après coup. Elle passe par l'état de séance et non par le canal de
   * données, pour la raison qui y range déjà la pause : le canal ne porte que
   * des gestes, et un fait de séance appartient au service (voir
   * `live-frame-codec.ts`).
   *
   * # La frontière est une position, pas une heure d'arrivée
   *
   * Le compteur change dans le même état que `positionMs` et `atMs` : la prise
   * commence là où l'interprète a repris *dans la musique*, et non à l'instant
   * où la métadonnée est arrivée — le service amortit ses écritures, et
   * plusieurs centaines de millisecondes séparent les deux.
   *
   * Jamais moins de 1, absent compris : une séance ouverte avant ce champ est
   * sur sa première prise, pas sur une prise numéro zéro.
   */
  take: number;
  /**
   * Le délai commun du jam, en millisecondes : ce que tout le monde pose sur
   * son transport quand le jam tourne. `null` quand la charge ne le porte pas,
   * ce qui est le cas hors jam.
   *
   * Le seul délai qui se publie encore, parce que c'est le seul que toute la
   * salle doit poser en même temps : si deux joueurs le recevaient chacun au
   * rythme de ses annonces, ils joueraient un moment sur deux grilles. Le
   * service le republie aussitôt qu'il change.
   */
  jamDelayMs: number | null;
  /**
   * Combien de participants le service voit, soi compris : ceux dont une
   * annonce lui est parvenue récemment. `null` tant qu'il n'a vu personne.
   *
   * Reporté au plus toutes les dix secondes, et seulement quand il change :
   * personne n'a besoin de voir les entrées et les sorties à l'instant. C'est
   * le nombre des compteurs de l'interface ; voir `roomHeadcount`.
   */
  audience: number | null;
  /**
   * Le cadrage de chaque caméra de la séance, par identité LiveKit de qui la
   * publie. Vide tant que personne n'a rien cadré.
   *
   * Publié pour tout le monde, et pas seulement pour l'appareil concerné :
   * c'est ce qui fait que le téléphone le retrouve en se reconnectant, et que
   * l'écran de l'interprète montre ce qu'il a réglé sans l'avoir gardé chez
   * lui. Voir {@link CameraFraming} — rien de tout cela ne se pose à
   * l'affichage.
   */
  cameras: CameraFramings;
}

/**
 * Lit la charge publiée, ou rend `null`.
 *
 * Tolérante par construction : cette chaîne vient d'un service qu'on ne
 * contrôle pas au même rythme que le studio, et une version plus récente peut
 * y ajouter des champs. On ne valide donc que ce dont on se sert, et on ignore
 * le reste plutôt que d'échouer sur ce qu'on ne comprend pas.
 *
 * Seuls la position et son instant sont indispensables : sans eux il n'y a
 * pas de séance à suivre. Tout le reste vaut son défaut quand il manque ou
 * qu'il est illisible — un lead absent est un lead vacant, un délai du jam ou
 * une audience absents valent `null`, une prise absente est la première — et ne
 * fait jamais perdre la position.
 *
 * La table `peers` d'une version antérieure est ignorée si elle traîne
 * encore : plus aucun délai ne s'y lit.
 */
export function parseSessionState(payload: string): LiveSessionState | null {
  if (payload === "") {
    return null;
  }

  try {
    const raw = JSON.parse(payload) as Record<string, unknown>;

    const positionMs = finiteNumber(raw.position_ms);
    const atMs = finiteNumber(raw.at_ms);

    if (positionMs === null || atMs === null) {
      return null;
    }

    return {
      positionMs,
      atMs,
      paused: raw.paused === true,
      tempoBpm: finiteNumber(raw.tempo_bpm) ?? 0,
      tempoMarks: parseTempoMarks(raw.tempo_marks),
      signature: parseSessionSignature(raw.signature),
      rev: finiteNumber(raw.rev) ?? 0,
      jam: raw.jam === true,
      lead: typeof raw.lead === "string" ? raw.lead : "",
      measuresHidden: raw.measures_hidden === true,
      curtain: raw.curtain === true,
      chatClosed: raw.chat_closed === true,
      // Le plancher tient les deux cas où le champ ne dit rien : une séance
      // ouverte avant lui, et une charge qui l'aurait à zéro. Les deux sont la
      // première prise.
      take: Math.max(1, Math.floor(finiteNumber(raw.take) ?? 1)),
      jamDelayMs: durationOrNull(raw.jam_delay_ms),
      audience: countOrNull(raw.audience),
      cameras: parseCameraFramings(raw.cameras),
    };
  } catch {
    return null;
  }
}

/**
 * Le délai que **ce** participant pose sur son transport, en millisecondes.
 *
 * # Deux sources, et le régime choisit
 *
 * En jam, le délai commun publié pour la salle ; sinon, `privateDelayMs`, ce
 * que le service a répondu à notre dernière annonce partie (voir
 * `PrivateDelay`). Rien d'autre ne s'y mêle — ni moyenne, ni calcul sur les
 * jambes : le service sait quel régime tourne et ce qu'il implique pour qui,
 * et le client pose le nombre.
 *
 * La réponse à l'annonce porte aussi le délai commun, en jam. Mais chacun la
 * reçoit au rythme de ses propres annonces, décalé d'une demi-seconde au plus
 * sur son voisin : quand le délai commun monte, deux joueurs le poseraient
 * chacun à son tour et joueraient ce temps-là sur deux grilles. L'état publié
 * arrive à toute la salle d'un coup.
 *
 * Zéro faute de mieux — en jam sans délai publié, ou sans réponse reçue :
 * c'est le seul défaut qui ne retarde personne. Un arrivant a le sien dès la
 * réponse à sa première annonce, qui part à la connexion.
 */
export function ownDelayMs(state: LiveSessionState | null, privateDelayMs: number | null): number {
  if (state !== null && state.jam) {
    return state.jamDelayMs ?? 0;
  }

  return privateDelayMs ?? 0;
}

/**
 * Combien nous sommes dans la salle, soi compris : le nombre des compteurs.
 *
 * L'audience du service quand l'état la porte ; sinon la liste de LiveKit,
 * qui ne compte que les autres, plus soi. Le repli tient le temps que le
 * service reporte sa première audience, et face à un service qui ne la publie
 * pas encore.
 *
 * # Pourquoi LiveKit n'est plus la source
 *
 * Un spectateur de récital ne lui est plus visible : LiveKit n'annonce plus
 * son arrivée ni son départ au reste de la salle, qui les recevait tous. Sa
 * liste ne compte donc plus le public ; le service, qui reçoit l'annonce de
 * chacun, le compte.
 *
 * Jamais moins d'un : on y est. Une audience qui ne nous compte pas encore —
 * notre première annonce en vol, pas encore reportée — ne doit pas montrer une
 * salle vide à celui qui s'y trouve.
 */
export function roomHeadcount(state: LiveSessionState | null, othersSeenByLiveKit: number): number {
  const audience = state?.audience ?? null;

  if (audience === null) {
    return othersSeenByLiveKit + 1;
  }

  return Math.max(1, audience);
}

/**
 * Ce participant a-t-il le droit d'émettre ses gestes.
 *
 * En jam, tout le monde ; sinon, le leader seul. Un lead vacant n'est le lead
 * de personne — pas même d'une identité vide, d'où la garde explicite : sans
 * elle, `"" === ""` ferait émettre quiconque n'a pas d'identité, précisément
 * le cas où l'on sait le moins qui l'on est.
 *
 * Sans état, personne : il n'y a pas de régime connu, donc pas de droit. La
 * fenêtre est celle de la première lecture de la métadonnée, pendant laquelle
 * la position n'est de toute façon pas encore alignée.
 */
export function canEmit(state: LiveSessionState | null, identity: string): boolean {
  if (state === null) {
    return false;
  }

  if (state.jam) {
    return true;
  }

  return state.lead !== "" && state.lead === identity;
}

/**
 * La position de la séance maintenant, sur l'horloge locale.
 *
 * `clockOffsetMs` est l'écart mesuré : ce qu'il faut ajouter à notre horloge
 * pour obtenir celle du service. Sans lui, `atMs` serait un nombre sans
 * rapport avec le nôtre — c'est cette conversion qui donne son sens à la
 * mesure.
 *
 * En pause, la position est figée : elle ne dépend plus du temps qui passe, et
 * c'est tout ce que « en pause » veut dire ici.
 */
export function positionNowMs(
  state: LiveSessionState,
  localNowMs: number,
  clockOffsetMs: number,
): number {
  if (state.paused) {
    return state.positionMs;
  }

  const serverNowMs = localNowMs + clockOffsetMs;

  return state.positionMs + Math.max(0, serverNowMs - state.atMs);
}

/**
 * Ce que ce pair entend quand on joue, en millisecondes après la touche.
 *
 * # Ce n'est plus une hypothèse
 *
 * Le trajet est la moitié de chaque jambe : rien ne va d'un participant à un
 * autre, tout transite par le SFU, et les jambes se composent exactement (voir
 * `LiveTransport.localRoundTripMs`). Mais en séance la note ne sonne pas à
 * l'arrivée — elle est programmée à son tick, que la tête de lecture du pair
 * atteint **son** délai après le geste.
 *
 * Le délai est le sien, tel que le service le rend dans sa ligne de la route
 * des participants — pas une supposition sur ce qu'il applique, et pas le
 * nôtre : en régime de lead, chacun a le sien. Le panneau a supposé auparavant
 * que le pair appliquait le même budget que nous ; c'était vrai quand ça
 * l'était, et rien ne le disait quand ça ne l'était plus.
 *
 * # Pourquoi sa sortie audio ne s'ajoute plus
 *
 * Elle le faisait, et c'était juste tant que la grille datait la trame *rendue*.
 * Chacun programme désormais un tampon de sortie **avant** l'instant de grille,
 * pour que ce soit l'oreille qui tombe dessus (voir
 * `LocalTransport.getTimeFromTick`) : le tampon est absorbé par le délai, pas
 * ajouté après lui. L'additionner comptait deux fois une grandeur que le
 * service budgète déjà, et gonflait le chiffre affiché d'une vingtaine de
 * millisecondes qui n'existent pas.
 *
 * Il reste dans le `Math.max`, pour le seul cas où le tampon ne tient pas : un
 * délai qui ne couvre pas `trajet + sortie` ne laisse pas la place de
 * programmer en avance, la note part au plus tôt, et c'est alors le réseau plus
 * la carte son qui gouvernent. C'est aussi ce que vaut un délai nul — le
 * leader, ou une séance dont l'état n'est pas encore lu.
 *
 * L'ordonnancement du pair n'entre pas dans la somme : une note programmée à un
 * instant absolu n'est pas décalée par sa fenêtre d'anticipation (voir
 * `lib/audio/tone-context.ts`). Il reste affiché parce qu'il mesure le hoquet
 * que sa machine s'attend à subir, pas parce qu'il retarde la note.
 *
 * N'a de sens que si l'on émet : un auditeur n'est entendu de personne.
 */
export function heardMs(ownLegMs: number, peer: LivePeerInfo): number {
  return Math.max((ownLegMs + peer.legMs) / 2 + peer.outputMs, peer.delayMs);
}
